import { Box, HoverCard, Link, Text } from '@workos-inc/design-system';
import * as React from 'react';
import { useKeysQuery, UsersOrganizationsRole } from 'src/graphql/generated';
import { useCurrentSession, useUser } from 'src/queries';
import { CODE_BLOCK_REPLACEMENTS } from 'src/utils/code-block-replacements';
interface CodeBlockReplacement {
  pattern?: string;
}
export const CodeBlockReplacement: React.FC<Readonly<CodeBlockReplacement>> = ({
  pattern
}) => {
  if (pattern === CODE_BLOCK_REPLACEMENTS.clientId.match) {
    return <ApiClientId />;
  }
  if (pattern === CODE_BLOCK_REPLACEMENTS.secretKey.match) {
    return <ApiSecretKey />;
  }
  if (pattern === CODE_BLOCK_REPLACEMENTS.authkitDomain.match) {
    return <AuthkitDomain />;
  }
  if (pattern === CODE_BLOCK_REPLACEMENTS.customAuthDomain.match) {
    return <CustomAuthDomain />;
  }
  return null;
};
const Content = ({
  children
}: {
  children?: React.ReactNode;
}) => <HoverCard.Content side="top" size="1" data-sentry-element="HoverCard.Content" data-sentry-component="Content" data-sentry-source-file="code-block-replacement.tsx">
    <Box maxWidth="240px" data-sentry-element="Box" data-sentry-source-file="code-block-replacement.tsx">
      <Text as="p" size="2" trim="both" data-sentry-element="Text" data-sentry-source-file="code-block-replacement.tsx">
        {children}
      </Text>
    </Box>
  </HoverCard.Content>;
const ApiClientId = () => {
  const {
    data: currentProject
  } = useCurrentSession({
    select: session => session.currentEnvironment.project
  });
  const firstSandboxEnvironment = currentProject?.environments.at(0);
  if (firstSandboxEnvironment) {
    return <HoverCard.Root>
        <Content>
          This is the client ID associated with your{' '}
          {firstSandboxEnvironment.name} environment.
        </Content>

        <HoverCard.Trigger>
          <Link color="blue" href={CODE_BLOCK_REPLACEMENTS.clientId.href}>
            {firstSandboxEnvironment.clientId}
          </Link>
        </HoverCard.Trigger>
      </HoverCard.Root>;
  }
  return <HoverCard.Root data-sentry-element="HoverCard.Root" data-sentry-component="ApiClientId" data-sentry-source-file="code-block-replacement.tsx">
      <Content data-sentry-element="Content" data-sentry-source-file="code-block-replacement.tsx">
        Replace this with your client ID found on the{' '}
        <Link href={`${process.env.NEXT_PUBLIC_DASHBOARD_URL}/api-keys`} data-sentry-element="Link" data-sentry-source-file="code-block-replacement.tsx">
          API Keys
        </Link>{' '}
        page in the dashboard.
      </Content>

      <HoverCard.Trigger data-sentry-element="HoverCard.Trigger" data-sentry-source-file="code-block-replacement.tsx">
        <Link color="gray" href={CODE_BLOCK_REPLACEMENTS.clientId.href} data-sentry-element="Link" data-sentry-source-file="code-block-replacement.tsx">
          {CODE_BLOCK_REPLACEMENTS.clientId.match}
        </Link>
      </HoverCard.Trigger>
    </HoverCard.Root>;
};
const ApiSecretKey = () => {
  const {
    data: currentProject
  } = useCurrentSession({
    select: session => session.currentEnvironment.project
  });
  const {
    data: isSupportRole = true
  } = useUser({
    select: me => me.teamMemberships[0]?.role === UsersOrganizationsRole.Support
  });
  const firstSandboxEnvironment = currentProject?.environments[0];
  const {
    data: defaultKey
  } = useKeysQuery({
    environmentId: firstSandboxEnvironment?.id ?? ''
  }, {
    staleTime: Infinity,
    select: data => data.keys.data[0],
    enabled: Boolean(firstSandboxEnvironment?.id && !isSupportRole)
  });
  if (!isSupportRole && defaultKey && firstSandboxEnvironment) {
    return <HoverCard.Root>
        <Content>
          This is the secret key associated with your{' '}
          {firstSandboxEnvironment.name} environment.
        </Content>

        <HoverCard.Trigger>
          <Link color="blue" data-dd-privacy="mask" href={CODE_BLOCK_REPLACEMENTS.secretKey.href}>
            {defaultKey.displayValue}
          </Link>
        </HoverCard.Trigger>
      </HoverCard.Root>;
  }
  return <HoverCard.Root data-sentry-element="HoverCard.Root" data-sentry-component="ApiSecretKey" data-sentry-source-file="code-block-replacement.tsx">
      <Content data-sentry-element="Content" data-sentry-source-file="code-block-replacement.tsx">
        Replace this with your secret key found on the{' '}
        <Link href={`${process.env.NEXT_PUBLIC_DASHBOARD_URL}/api-keys`} data-sentry-element="Link" data-sentry-source-file="code-block-replacement.tsx">
          API Keys
        </Link>{' '}
        page in the dashboard.
      </Content>

      <HoverCard.Trigger data-sentry-element="HoverCard.Trigger" data-sentry-source-file="code-block-replacement.tsx">
        <Link color="gray" href={CODE_BLOCK_REPLACEMENTS.secretKey.href} data-sentry-element="Link" data-sentry-source-file="code-block-replacement.tsx">
          {CODE_BLOCK_REPLACEMENTS.secretKey.match}
        </Link>
      </HoverCard.Trigger>
    </HoverCard.Root>;
};
const AuthkitDomain = () => {
  const {
    data: currentProject
  } = useCurrentSession({
    select: session => session.currentEnvironment.project
  });
  const firstSandboxEnvironment = currentProject?.environments[0];
  const authkitDomain = firstSandboxEnvironment?.preferredAuthkitDomain?.domain;
  if (authkitDomain) {
    return <HoverCard.Root>
        <Content>
          This is the AuthKit domain associated with your{' '}
          {firstSandboxEnvironment.name} environment.
        </Content>

        <HoverCard.Trigger>
          <Link color="blue" data-dd-privacy="mask" href={`${CODE_BLOCK_REPLACEMENTS.authkitDomain.href}/environments/${firstSandboxEnvironment.id}/authentication`}>
            {authkitDomain}
          </Link>
        </HoverCard.Trigger>
      </HoverCard.Root>;
  }
  const dashboardPath = firstSandboxEnvironment ? `/environments/${firstSandboxEnvironment.id}/authentication` : '';
  return <HoverCard.Root data-sentry-element="HoverCard.Root" data-sentry-component="AuthkitDomain" data-sentry-source-file="code-block-replacement.tsx">
      <Content data-sentry-element="Content" data-sentry-source-file="code-block-replacement.tsx">
        Replace this with the domain found in the Hosted UI section of the{' '}
        <Link href={`${process.env.NEXT_PUBLIC_DASHBOARD_URL}${dashboardPath}`} data-sentry-element="Link" data-sentry-source-file="code-block-replacement.tsx">
          Authentication
        </Link>{' '}
        page in the dashboard.
      </Content>

      <HoverCard.Trigger data-sentry-element="HoverCard.Trigger" data-sentry-source-file="code-block-replacement.tsx">
        <Link color="gray" href={`${CODE_BLOCK_REPLACEMENTS.authkitDomain.href}${dashboardPath}`} data-sentry-element="Link" data-sentry-source-file="code-block-replacement.tsx">
          {'<subdomain>.authkit.app'}
        </Link>
      </HoverCard.Trigger>
    </HoverCard.Root>;
};
const CustomAuthDomain = () => {
  const {
    data
  } = useCurrentSession({
    select: session => ({
      currentProject: session.currentEnvironment.project
    })
  });
  const {
    currentProject
  } = data ?? {};
  const firstSandboxEnvironment = currentProject?.environments[0];
  const apiDomain = firstSandboxEnvironment?.customAuthDomain?.domain ?? 'api.workos.com';
  if (firstSandboxEnvironment) {
    return <HoverCard.Root>
        <Content>
          This is the API domain associated with your{' '}
          {firstSandboxEnvironment.name} environment.
        </Content>

        <HoverCard.Trigger>
          <Link color="blue" data-dd-privacy="mask" href={`${CODE_BLOCK_REPLACEMENTS.customAuthDomain.href}/environments/${firstSandboxEnvironment.id}/authentication`}>
            {apiDomain}
          </Link>
        </HoverCard.Trigger>
      </HoverCard.Root>;
  }
  return <HoverCard.Root data-sentry-element="HoverCard.Root" data-sentry-component="CustomAuthDomain" data-sentry-source-file="code-block-replacement.tsx">
      <Content data-sentry-element="Content" data-sentry-source-file="code-block-replacement.tsx">
        Replace this with the domain found in the Authentication API section of
        the <Link href={process.env.NEXT_PUBLIC_DASHBOARD_URL} data-sentry-element="Link" data-sentry-source-file="code-block-replacement.tsx">Domains</Link>{' '}
        page in the dashboard.
      </Content>

      <HoverCard.Trigger data-sentry-element="HoverCard.Trigger" data-sentry-source-file="code-block-replacement.tsx">
        <Link color="gray" href={`${CODE_BLOCK_REPLACEMENTS.customAuthDomain.href}`} data-sentry-element="Link" data-sentry-source-file="code-block-replacement.tsx">
          {apiDomain}
        </Link>
      </HoverCard.Trigger>
    </HoverCard.Root>;
};